import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { Link } from '@tanstack/react-router';
import { ArrowRight, Bot, DollarSign, GraduationCap, Plane, Target } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, memo, useMemo } from 'react';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { motion } from 'framer-motion';
import { MotionButton } from '../ui/motion-button';
import { slideInFromBottom, staggerContainer, staggerItem, viewportOptions } from '@/lib/animations';
import debounce from 'lodash/debounce';


// Define interfaces for our components
interface ExpandableCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface GrowthDataPoint {
  year: number;
  conservative: number;
  expected: number;
  optimistic: number;
}

// BenefitCard component for the comparison cards - memoized to prevent unnecessary re-renders
const BenefitCard = memo(({ icon, title, description }: ExpandableCardProps) => {
  return (
    <motion.div
      variants={staggerItem}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
    >
      <Card>
        <CardHeader className="flex flex-row items-start gap-4 space-y-0">
          <div className="bg-brand/10 p-3 rounded-xl">
            {icon}
          </div>
          <div>
            <CardTitle className="text-lg mb-1 text-black">{title}</CardTitle>
            <p className="text-gray-700 text-sm">{description}</p>
          </div>
        </CardHeader>
      </Card>
    </motion.div>
  );
});

BenefitCard.displayName = 'BenefitCard';

// Custom tooltip for the chart - memoized to prevent re-renders
interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    // Payload can have other properties like dataKey, name, color, stroke, etc.
  }>;
  label?: string | number;
}
const CustomTooltip = memo(({ active, payload, label }: CustomTooltipProps) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 rounded-lg shadow-md border border-gray-100 text-xs">
        <p className="font-medium">Year {label}</p>
        <div className="space-y-1 mt-1">
          <div className="flex items-center justify-between gap-3">
            <span className="text-gray-500">Optimistic</span>
            <span className="font-medium">${payload[2].value.toLocaleString()}</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="font-medium text-green-700">Expected</span>
            <span className="font-bold text-green-700">${payload[1].value.toLocaleString()}</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-gray-500">Conservative</span>
            <span className="font-medium">${payload[0].value.toLocaleString()}</span>
          </div>
        </div>
      </div>
    );
  }
  return null;
});

CustomTooltip.displayName = 'CustomTooltip';

// Counter display component - only re-renders when count changes
const AnimatedCounter = memo(({ targetValue }: { targetValue: number }) => {
  const [count, setCount] = useState(0);
  const [showCounter, setShowCounter] = useState(false);
  const counterRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const currentCounterRef = counterRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setShowCounter(true);
        }
      },
      { threshold: 0.1 }
    );

    if (currentCounterRef) {
      observer.observe(currentCounterRef);
    }

    return () => {
      if (currentCounterRef) {
        observer.unobserve(currentCounterRef);
      }
    };
  }, []);

  useEffect(() => {
    if (showCounter) {
      let start = 0;
      const duration = 2000;
      const increment = Math.ceil(targetValue / (duration / 16));

      const timer = setInterval(() => {
        start += increment;
        if (start >= targetValue) {
          setCount(targetValue);
          clearInterval(timer);
        } else {
          setCount(start);
        }
      }, 16);

      return () => clearInterval(timer);
    }
  }, [showCounter, targetValue]);

  return (
    <span ref={counterRef} className="relative z-10 text-brand">
      ${count.toLocaleString()}
    </span>
  );
});

AnimatedCounter.displayName = 'AnimatedCounter';

// Chart component - memoized to prevent re-renders when other parts change
const InvestmentChart = memo(({ 
  chartData, 
  yAxisDomain, 
  debouncedYearsToRetirement,
  formatCurrencyValue 
}: {
  chartData: GrowthDataPoint[];
  yAxisDomain: [number, number];
  debouncedYearsToRetirement: number;
  formatCurrencyValue: (value: number) => string;
}) => {
  return (
    <div className="md:col-span-2 h-full min-h-[300px]">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
          <defs>
            <linearGradient id="colorRangeArea" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#10B981" stopOpacity={0.2} />
              <stop offset="95%" stopColor="#10B981" stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="year"
            axisLine={false}
            tickLine={false}
            tickFormatter={(value: number) => value === 0 ? 'Now' : value === debouncedYearsToRetirement ? `${debouncedYearsToRetirement}y` : ''}
            tick={{ fontSize: 10 }}
            dy={5}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            domain={yAxisDomain}
            tickFormatter={formatCurrencyValue}
            tick={{ fontSize: 10 }}
            width={40}
            allowDataOverflow={false}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area
            type="monotone"
            dataKey="conservative"
            stroke="#D1FAE5"
            fill="none"
            strokeWidth={1}
            dot={false}
            animationDuration={300}
          />
          <Area
            type="monotone"
            dataKey="optimistic"
            stroke="none"
            fill="url(#colorRangeArea)"
            animationDuration={300}
          />
          <Area
            type="monotone"
            dataKey="expected"
            stroke="#10B981"
            strokeWidth={2}
            fill="none"
            dot={false}
            activeDot={{ r: 4, fill: "#047857" }}
            animationDuration={300}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
});

InvestmentChart.displayName = 'InvestmentChart';

// Savings summary component - memoized to prevent re-renders
const SavingsSummary = memo(({ 
  homeValue, 
  initialSavings, 
  finalExpectedValue 
}: {
  homeValue: number;
  initialSavings: number;
  finalExpectedValue: number;
}) => {
  return (
    <div className="bg-brand-lighter/30 rounded-lg p-4 flex flex-col justify-between">
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Traditional commission (2.5%):</span>
          <span className="font-medium">${(homeValue * 0.025).toLocaleString()}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Swim fee:</span>
          <span className="font-medium">$599</span>
        </div>
        <div className="border-t pt-2 flex justify-between">
          <span className="text-sm font-medium">Your savings:</span>
          <span className="text-lg font-bold text-primary">${initialSavings.toLocaleString()}</span>
        </div>
      </div>
      <div className="text-center mt-3 pt-3 border-t">
        <p className="text-sm text-muted-foreground">By retirement, could grow to</p>
        <p className="text-2xl font-bold text-green-600">${finalExpectedValue.toLocaleString()}</p>
        <p className="text-xs text-muted-foreground">at 8% annual return</p>
      </div>
    </div>
  );
});

SavingsSummary.displayName = 'SavingsSummary';

export default function CostComparison() {

  // Investment calculator states
  const [homeValue, setHomeValue] = useState(800000);
  const [yearsToRetirement, setYearsToRetirement] = useState(20);

  // Debounced values for chart rendering to improve performance
  const [debouncedHomeValue, setDebouncedHomeValue] = useState(homeValue);
  const [debouncedYearsToRetirement, setDebouncedYearsToRetirement] = useState(yearsToRetirement);
  const [chartData, setChartData] = useState<GrowthDataPoint[]>([]);

  // Create debounced setters
  const debouncedSetHomeValue = useRef(debounce(setDebouncedHomeValue, 300)).current;
  const debouncedSetYearsToRetirement = useRef(debounce(setDebouncedYearsToRetirement, 300)).current;

  // Trigger debounced updates when values change
  useEffect(() => {
    debouncedSetHomeValue(homeValue);
  }, [homeValue, debouncedSetHomeValue]);

  useEffect(() => {
    debouncedSetYearsToRetirement(yearsToRetirement);
  }, [yearsToRetirement, debouncedSetYearsToRetirement]);

  // Fixed return rates
  const conservativeRate = 6;
  const expectedRate = 8;
  const optimisticRate = 10;

  // Calculate the initial savings (realtor fees avoided) - use immediate value for UI responsiveness
  const initialSavings = Math.max(0, (homeValue * 0.025) - 599);

  // Generate investment growth data for chart using debounced values
  const generateGrowthData = useCallback((): GrowthDataPoint[] => {
    const data: GrowthDataPoint[] = [];
    const debouncedInitialSavings = Math.max(0, (debouncedHomeValue * 0.025) - 599);

    for (let year = 0; year <= debouncedYearsToRetirement; year++) {
      const conservative = Math.round(debouncedInitialSavings * Math.pow(1 + (conservativeRate / 100), year));
      const expected = Math.round(debouncedInitialSavings * Math.pow(1 + (expectedRate / 100), year));
      const optimistic = Math.round(debouncedInitialSavings * Math.pow(1 + (optimisticRate / 100), year));
      data.push({
        year,
        conservative,
        expected,
        optimistic
      });
    }
    return data;
  }, [debouncedYearsToRetirement, debouncedHomeValue, conservativeRate, expectedRate, optimisticRate]);

  // Update chart data when debounced values change
  useEffect(() => {
    const newData = generateGrowthData();
    setChartData(newData);
  }, [generateGrowthData]);

  // Memoize expensive calculations
  const finalValues = useMemo(() => {
    return chartData.length > 0 ? {
      expected: chartData[chartData.length - 1].expected,
      conservative: chartData[chartData.length - 1].conservative,
      optimistic: chartData[chartData.length - 1].optimistic
    } : { expected: 0, conservative: 0, optimistic: 0 };
  }, [chartData]);


  // Helper function to calculate a nice upper bound for the y-axis
  const calculateNiceUpperBound = (value: number): number => {
    if (value < 10000) return Math.ceil(value / 5000) * 5000;
    if (value < 100000) return Math.ceil(value / 25000) * 25000;
    if (value < 500000) return Math.ceil(value / 100000) * 100000;
    if (value < 1000000) return Math.ceil(value / 250000) * 250000;
    if (value < 5000000) return Math.ceil(value / 1000000) * 1000000;
    return Math.ceil(value / 5000000) * 5000000;
  };

  const yAxisDomain: [number, number] = useMemo(() => {
    return chartData.length > 0
      ? [0, calculateNiceUpperBound(chartData[chartData.length - 1].optimistic)]
      : [0, 50000];
  }, [chartData]);

  // Format large currency values for display - memoized
  const formatCurrencyValue = useCallback((value: number): string => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}k`;
    } else {
      return `$${value}`;
    }
  }, []);



  return (
    <section className="py-16 md:py-20 relative bg-transparent overflow-hidden">
      {/* Subtle pattern overlay */}
      <div className="absolute inset-0 bg-grid-primary/[0.03] bg-size-[20px_20px] opacity-70"></div>

      <div className="max-w-6xl mx-auto space-y-16">
        {/* Title Section with Counter */}
        <motion.div
          className="max-w-3xl mx-auto text-center"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOptions}
        >
          <motion.span
            className="inline-block px-3 py-1 bg-secondary/20 text-secondary rounded-full text-sm font-medium mb-3"
            variants={staggerItem}
          >
            Cost Calculator
          </motion.span>
          <motion.h2
            className="text-4xl md:text-5xl font-bold text-black mb-4"
            variants={staggerItem}
          >
            You will pay{' '}
            <div className="relative inline-block">
              <AnimatedCounter targetValue={161000} />
              <div className="absolute -bottom-2 left-0 right-0 h-3 bg-brand/10 -rotate-2" />
            </div>
            <br />of realtor fees in your lifetime
          </motion.h2>
          <motion.p
            className="text-xl text-gray-700"
            variants={staggerItem}
          >
            That's a lot of hard-earned equity lost.
          </motion.p>
        </motion.div>

        {/* Top Section - Mission/Why Swim and Calculator in 2 columns */}
        <div className="grid md:grid-cols-12 gap-8 h-full">
          {/* Left Column - Mission & Technology in a vertical stack */}
          <motion.div
            className="md:col-span-5 flex flex-col h-full space-y-4"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOptions}
          >
            {/* Mission Section */}
            <motion.div variants={staggerItem}>
              <Card>
                <CardHeader className="flex flex-row items-start gap-4">
                  <div className="bg-brand/10 p-3 rounded-xl">
                    <Target className="h-6 w-6 text-brand" />
                  </div>
                  <div>
                    <CardTitle className="text-xl mb-2 text-black">Our Mission</CardTitle>
                    <p className="text-brand-dark">
                      At Swim, we believe homeowners deserve to keep more of their money. Our AI-powered platform reduces commissions, streamlines the selling process, and gives Canadians control over their real estate journey.
                    </p>
                  </div>
                </CardHeader>
              </Card>
            </motion.div>

            {/* Technology Section */}
            <motion.div variants={staggerItem}>
              <Card>
                <CardHeader className="flex flex-row items-start gap-4">
                  <div className="bg-brand/10 p-3 rounded-xl">
                    <Bot className="h-6 w-6 text-brand" />
                  </div>
                  <div>
                    <CardTitle className="text-xl mb-2 text-black">Why Swim?</CardTitle>
                    <p className="text-brand-dark">
                      By cutting out unnecessary costs and automating the heavy lifting, we deliver maximum value at a fraction of the price. Keep thousands in your pocket and sell your home on your terms.
                    </p>
                  </div>
                </CardHeader>
              </Card>
            </motion.div>
          </motion.div>

          {/* Right Column - Investment Calculator */}
          <motion.div
            variants={slideInFromBottom}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOptions}
            className="md:col-span-7 h-full flex flex-col"
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-xl text-black">Save Thousands On Your Next Home Sale</CardTitle>
              </CardHeader>
              <CardContent className="grow flex flex-col">
                {/* Input Controls */}
                <div className="space-y-3 mb-4">
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <label htmlFor="homeValue" className="text-sm font-medium">Home Value</label>
                      <span className="text-sm font-medium text-primary">${homeValue.toLocaleString()}</span>
                    </div>
                    <Slider
                      value={[homeValue]}
                      onValueChange={(value) => setHomeValue(value[0])}
                      min={100000}
                      max={2000000}
                      step={10000}
                      className="w-full"
                    />
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <label htmlFor="yearsToRetirement" className="text-sm font-medium">Years Until Retirement</label>
                      <span className="text-sm font-medium text-primary">{yearsToRetirement} years</span>
                    </div>
                    <Slider
                      value={[yearsToRetirement]}
                      onValueChange={(value) => setYearsToRetirement(value[0])}
                      min={5}
                      max={40}
                      step={1}
                      className="w-full"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4 flex-1">
                  <SavingsSummary 
                    homeValue={homeValue}
                    initialSavings={initialSavings}
                    finalExpectedValue={finalValues.expected}
                  />
                  <InvestmentChart 
                    chartData={chartData}
                    yAxisDomain={yAxisDomain}
                    debouncedYearsToRetirement={debouncedYearsToRetirement}
                    formatCurrencyValue={formatCurrencyValue}
                  />
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Bottom Section - "Ready to reclaim" and Benefits cards */}
        <motion.div
          variants={slideInFromBottom}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOptions}
        >
          <Card className="mt-8">
            <CardContent className="grid md:grid-cols-2 gap-8 p-6 md:p-8">
              {/* Left - Ready to reclaim */}
              <motion.div
                className="flex flex-col justify-center"
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={viewportOptions}
              >
                <motion.h3
                  className="text-3xl font-bold mb-4 text-black"
                  variants={staggerItem}
                >
                  Ready to reclaim your ${Math.round(initialSavings / 1000)}k?
                </motion.h3>
                <motion.p
                  className="text-lg text-gray-700 mb-6"
                  variants={staggerItem}
                >
                  Don't let fees eat away at your savings. With Swim, you keep more of your home's value. Sell smarter, sell for less, and keep what's yours.
                </motion.p>
                <motion.div variants={staggerItem}>
                  <MotionButton size="lg" className="bg-primary hover:bg-primary/90" asChild>
                    <Link to="/sellers-guide">
                      Learn How to Swim <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                  </MotionButton>
                </motion.div>
              </motion.div>

              {/* Right - Benefit Cards */}
              <motion.div
                className="space-y-4"
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={viewportOptions}
              >
                <BenefitCard
                  icon={<DollarSign className="h-6 w-6 text-primary" />}
                  title="Years of Retirement"
                  description="When you sell with Swim, you get a healthy rainy day fund"
                />

                <BenefitCard
                  icon={<GraduationCap className="h-6 w-6 text-primary" />}
                  title="Complete Education"
                  description="Fund your children's entire university education with the money you save"
                />

                <BenefitCard
                  icon={<Plane className="h-6 w-6 text-primary" />}
                  title="201 Trips to Rome"
                  description="Round trips from Toronto to Rome, Italy - it's up to you"
                />
              </motion.div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>
  );
}